/** @format */

import ReactImageMagnify from 'react-image-magnify';
import {makePictureTree} from 'filestack-adaptive';
import AdaptiveImage from '../adaptive_image';
import ReactDOM from 'react-dom';
import './magnify_image.sass';
import React from 'react';

import {If} from '../If';

const withZoomHandler = ComponentContainer => {
    return class extends React.Component {
        constructor(props) {
            super(props);
            this.state = {
                scale: 1,
                showPortal: false
            };

            this.magnifyRef = React.createRef();
        }

        componentDidMount() {
            if (this.magnifyRef.current) {
                this.magnifyRef.current.addEventListener('wheel', this.handleScrollZoom, {
                    passive: false
                });
            }

            if (!document.getElementById('magnify-portal')) {
                this.portal = document.createElement('div');
                this.portal.id = 'magnify-portal';
                this.portal.classList.add('bs4', 'bs4-grid');
                document.body.appendChild(this.portal);
            } else {
                this.portal = document.getElementById('magnify-portal');
            }

            // Needed on did update
            this.portal.addEventListener('wheel', this.handleScrollZoom, {
                passive: false
            });
        }

        componentWillUnmount() {
            if (this.magnifyRef.current) {
                this.magnifyRef.current.removeEventListener('wheel', this.handleScrollZoom);
            }
            if (typeof this.portal !== 'undefined') {
                this.portal.removeEventListener('wheel', this.handleScrollZoom);
            }
        }

        handleScrollZoom = e => {
            e.preventDefault();
            this.setState(state => {
                const {deltaY} = e;
                const velocityFactor = Math.abs(deltaY) / 150;
                const step = velocityFactor;
                if (deltaY < 0) {
                    state.scale += step;
                } else {
                    state.scale -= step;
                }

                if (state.scale > 1) {
                    state.scale = 1;
                } else if (state.scale < 0.25) {
                    state.scale = 0.25;
                }
                return state;
            });
        };

        handleTogglePortal = () => {
            this.setState(state => {
                const showPortal = !state.showPortal;
                if (showPortal === true) {
                    document.body.style.height = '100%';
                    document.body.style.overflow = 'hidden';
                } else {
                    document.body.style.height = 'auto';
                    document.body.style.overflow = 'auto';
                }

                return {showPortal};
            });
        };

        render() {
            const props = {...this.props};

            props.onTogglePortal = this.handleTogglePortal;

            return (
                <ComponentContainer
                    {...props}
                    {...this.state}
                    magnifyRef={this.magnifyRef}
                    portal={this.portal}
                />
            );
        }
    };
};

export default withZoomHandler(
    ({
        smallWidth = 300,
        shouldHideHintAfterFirstActivation = false,
        isHintEnabled = true,
        width: srcWidth,
        height: srcHeight,
        magnifiedImageWidth = 2,
        magnifiedImageHeight = 1,
        ...props
    }) => {
        if (props.src === '') {
            return <div>File Missing</div>;
        }

        const backgroundStyle = {
            backgroundImage:
                'linear-gradient(45deg, #9a9a9a 25%, transparent 25%), linear-gradient(-45deg, #9a9a9a 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #9a9a9a 75%), linear-gradient(-45deg, transparent 75%, #9a9a9a 75%)',
            backgroundSize: '20px 20px',
            backgroundColor: 'white',
            backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
            zIndex: 199
        };

        const getBackgroundStyle = props.backgroundStyle || backgroundStyle;

        if (srcWidth === 0) {
            return (
                <div>
                    <div
                        ref={props.magnifyRef}
                        className="magnify-container"
                        style={{
                            position: 'relative',
                            width: smallWidth + 40,
                            padding: '20px',
                            ...getBackgroundStyle
                        }}>
                        <AdaptiveImage src={props.src} width={smallWidth} />
                    </div>
                </div>
            );
        }

        const width = Math.min(10000, parseInt(srcWidth)); // Max value is 10k
        const height = Math.min(10000, parseInt(srcHeight)); // Max value is 10k
        smallWidth = smallWidth > width ? width : smallWidth;
        const smallHeight = Math.ceil((height / width) * smallWidth);

        let longSide = smallWidth;
        if (props.showPortal) {
            longSide =
                window.innerWidth > window.innerHeight
                    ? Math.min(width, window.innerWidth * 0.9)
                    : Math.min(height, window.innerHeight * 0.9);
        }

        let shortSide =
            width > height
                ? Math.ceil((height / width) * longSide)
                : Math.ceil((width / height) * longSide);

        if (props.showPortal) {
            if (width > height) {
            }
        }

        const mgWidth = width > height ? longSide : shortSide;
        const mgHeight = width > height ? shortSide : longSide;

        const tree = makePictureTree(
            {apiKey: process.env.FILESTACK_KEY, srcHandle: props.src},
            {
                sizes: {
                    fallback: mgWidth + 'px'
                },
                resolutions: [mgWidth, width],
                cname: process.env.FILESTACK_CDN_URL?.replace('https://cdn.', '')
            }
        );

        const zoomFactor = 1 / props.scale;

        let lensDims = shortSide;

        const largeWidth = Math.ceil(Math.max(width * props.scale, mgWidth));
        const largeHeight = Math.ceil(Math.max(height * props.scale, mgHeight));

        const Magnifier = (
            <ReactImageMagnify
                {...{
                    enlargedImagePosition: window.innerWidth < 700 ? 'over' : 'beside',
                    enlargedImageContainerDimensions: {
                        width: lensDims * magnifiedImageWidth,
                        height: lensDims * magnifiedImageHeight
                    },
                    smallImage: {
                        width: mgWidth,
                        height: mgHeight,
                        src: tree.img.src,
                        srcSet: tree.img.srcSet,
                        sizes: smallWidth.toString()
                    },
                    largeImage: {
                        src: tree.img.src,
                        srcSet: tree.img.srcSet,
                        width: largeWidth,
                        height: largeHeight
                    },
                    fadeDurationInMs: 250,
                    hoverOffDelayInMs: 250,
                    isHintEnabled,
                    shouldHideHintAfterFirstActivation
                }}
            />
        );

        if (props.showPortal) {
            return ReactDOM.createPortal(
                <div className="portal" onClick={props.onTogglePortal}>
                    <div className="align-self-center">
                        <div
                            className="magnify-container"
                            style={{
                                width: width > height ? longSide + 40 : shortSide + 40,
                                padding: '20px',
                                ...backgroundStyle
                            }}>
                            {Magnifier}
                        </div>
                    </div>
                </div>,
                props.portal
            );
        }

        return (
            <div
                ref={props.magnifyRef}
                className="magnify-container"
                style={{
                    position: 'relative',
                    width: width > height ? longSide + 40 : shortSide + 40,
                    padding: '20px',
                    ...getBackgroundStyle
                }}>
                {Magnifier}
                <If condition={props.showDownload !== undefined ? props.showDownload : true}>
                    <div style={{position: 'absolute', right: 0, top: 0, zIndex: -1}}>
                        <a
                            tabIndex="-1"
                            className="btn btn-secondary btn-sm"
                            target="_blank"
                            href={props.downloadSrc || props.src}>
                            <i className="fa fa-download" />
                        </a>
                    </div>
                </If>
            </div>
        );
    }
);
